import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Eye } from "lucide-react";

export interface RosterOnlyRow {
  /** Index in the staged rosterData.employees array (for the edit modal). */
  rosterIdx: number;
  firstName: string;
  lastName: string;
  email: string | null;
  hireDate: string | null;
  whyRosterOnly: string;
}

/**
 * Step 1 — Block 3. Focused table showing ONLY the people unique to Step 1
 * (people in the Full Census who are NOT in the Enrollment Census). These
 * are the value Step 1 brings: waived, ineligible, terminated, etc.
 *
 * Empty state: explicit reassuring copy. Single-file platforms naturally
 * land in this state.
 *
 * Loading state (diff not yet computed because Step 2's file isn't
 * uploaded): show a deferred-state placeholder.
 */
export function RosterOnlyTable({
  rows,
  diffComputed,
  onEdit,
}: {
  rows: RosterOnlyRow[];
  diffComputed: boolean;
  onEdit?: (rosterIdx: number) => void;
}) {
  if (!diffComputed) {
    return (
      <Card>
        <CardContent className="p-4">
          <div className="text-[13px] font-semibold">Roster-only people</div>
          <div className="mt-1 text-[12.5px] text-muted-foreground">
            Computed once Step 2's enrollment census is uploaded.
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-0">
        <div className="flex items-start justify-between gap-3 border-b p-4">
          <div>
            <div className="text-[13px] font-semibold">
              {rows.length} roster-only{" "}
              {rows.length === 1 ? "person" : "people"}
            </div>
            <div className="mt-0.5 text-[12.5px] text-muted-foreground">
              {rows.length === 0
                ? "Everyone in the Full Census is also in the enrollment file."
                : "On the roster but not in the enrollment census. Click a row to edit before continuing."}
            </div>
          </div>
          {rows.length > 0 && (
            <Badge
              variant="outline"
              className="shrink-0 whitespace-nowrap"
              style={{
                background: "#FFFBEB",
                borderColor: "#FCD34D",
                color: "#92400E",
              }}
            >
              Unique to Step 1
            </Badge>
          )}
        </div>

        {rows.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/30">
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Hire date</TableHead>
                <TableHead>Why roster-only</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.rosterIdx}>
                  <TableCell className="font-medium">
                    {r.firstName} {r.lastName}
                  </TableCell>
                  <TableCell
                    className={
                      r.email ? "" : "italic text-muted-foreground"
                    }
                  >
                    {r.email ?? "—"}
                  </TableCell>
                  <TableCell className="font-mono text-xs">
                    {r.hireDate ?? "—"}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {r.whyRosterOnly}
                  </TableCell>
                  <TableCell className="text-right">
                    {onEdit && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2"
                        title="View / edit"
                        onClick={(e) => {
                          e.stopPropagation();
                          onEdit(r.rosterIdx);
                        }}
                      >
                        <Eye className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : null}
      </CardContent>
    </Card>
  );
}
